import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider, Helmet } from 'react-helmet-async';

import { useAuthStore, useGenreStore, useDomainStore } from './hooks/useStore';

import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Sidebar from './components/Sidebar/Sidebar';
import AdDisplay, { HeadCodeAds } from './components/AdDisplay/AdDisplay';

import Home from './pages/Home/Home';
import NovelList from './pages/NovelList/NovelList';
import NovelDetail from './pages/NovelDetail/NovelDetail';
import Reading from './pages/Reading/Reading';
import Search from './pages/Search/Search';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Profile from './pages/Profile/Profile';
import Bookmarks from './pages/Bookmarks/Bookmarks';
import Genres from './pages/Genres/Genres';
import NotFound from './pages/NotFound/NotFound';
import PageView from './pages/Page/PageView';

import {
  AdminLayout, Dashboard,
  NovelList as AdminNovelList, NovelForm,
  ChapterList, ChapterForm,
  GenreManagement, UserManagement, DomainManagement,
  AdList, AdForm, CrawlerPage, PageManagement
} from './pages/Admin';

import './styles/globals.css';

const SEOHead = () => {
  const { domain } = useDomainStore();
  if (!domain?.seo) return null;
  return (
    <Helmet>
      <title>{domain.seo.title || domain.name || 'NovelVibe'}</title>
      <meta name="description" content={domain.seo.description || ''} />
      <meta name="keywords" content={domain.seo.keywords || ''} />
      {domain.seo.ogImage && <meta property="og:image" content={domain.seo.ogImage} />}
      <meta property="og:title" content={domain.seo.title || domain.name} />
      <meta property="og:description" content={domain.seo.description || ''} />
      <meta property="og:type" content="website" />
    </Helmet>
  );
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return children;
  return (
    <>
      <Header />
      <AdDisplay position="below_header" className="below-header-ad" />
      <Sidebar />
      {children}
      <Footer />
    </>
  );
};

function App() {
  const { fetchUser } = useAuthStore();
  const { fetchGenres } = useGenreStore();
  const { fetchDomain } = useDomainStore();

  useEffect(() => {
    const { isAuthenticated, user } = useAuthStore.getState();

    // Nếu đã có session từ persist → KHÔNG gọi fetchUser
    // Tránh trường hợp getMe() lỗi (mạng, cookie, CORS...) rồi xóa state
    // Việc verify session thực sự xảy ra khi user thực hiện các API call khác
    if (!isAuthenticated || !user) {
      fetchUser();
    }

    fetchGenres();
    fetchDomain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <SEOHead />
        <div className="app">
          <AppLayout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/novels" element={<NovelList />} />
              <Route path="/novel/:slug" element={<NovelDetail />} />
              <Route path="/novel/:novelSlug/chapter/:chapterNumber" element={<Reading />} />
              <Route path="/search" element={<Search />} />
              <Route path="/genres" element={<Genres />} />
              <Route path="/genre/:slug" element={<NovelList />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/bookmarks" element={<Bookmarks />} />
              <Route path="/page/:slug" element={<PageView />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="novels" element={<AdminNovelList />} />
                <Route path="novels/new" element={<NovelForm />} />
                <Route path="novels/edit/:id" element={<NovelForm />} />
                <Route path="chapters" element={<ChapterList />} />
                <Route path="chapters/new" element={<ChapterForm />} />
                <Route path="chapters/edit/:id" element={<ChapterForm />} />
                <Route path="genres" element={<GenreManagement />} />
                <Route path="crawler" element={<CrawlerPage />} />
                <Route path="domains" element={<DomainManagement />} />
                <Route path="ads" element={<AdList />} />
                <Route path="ads/new" element={<AdForm />} />
                <Route path="ads/edit/:id" element={<AdForm />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="pages" element={<PageManagement />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppLayout>
          <HeadCodeAds />
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
              },
              success: { iconTheme: { primary: '#22c55e', secondary: 'white' } },
              error: { iconTheme: { primary: '#ef4444', secondary: 'white' } },
            }}
          />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
